import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, database, storage, createRealtimeListener } from '../firebase';
import { ref, set, push, remove, update } from 'firebase/database';
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { ArrowLeft, Plus, Upload, Check, Settings as SettingsIcon, Trash2, Archive, ArchiveRestore, MoreHorizontal, EyeOff, Eye, FileText, Github, RefreshCw, X as XIcon, Link as LinkIcon } from 'lucide-react';
import TodoDetailPage from './TodoDetailPage';
import { extractPdfText } from '../utils/pdfExtract';
import { parseRepoUrl, listUserRepos, fetchRepoSnapshot } from '../utils/github';
import './TodoBoardPage.css';

const TodoBoardPage = () => {
  const navigate = useNavigate();
  const [userId, setUserId] = useState(null);
  const [projects, setProjects] = useState([]);
  const [newTodoText, setNewTodoText] = useState({}); // { [projectId]: text }
  const [newTodoDate, setNewTodoDate] = useState({}); // { [projectId]: "YYYY-MM-DD" }
  const [todoMenu, setTodoMenu] = useState(null); // { x, y, projectId, todoIndex, dueDate }
  const [uploadingLogo, setUploadingLogo] = useState({}); // { [projectId]: bool }
  const [editingContext, setEditingContext] = useState(null); // projectId
  const [contextDraft, setContextDraft] = useState({ industry: '', bookContext: '' });
  const [githubIntegration, setGithubIntegration] = useState(null); // { accessToken, ... }
  const [githubRepos, setGithubRepos] = useState([]);
  const [githubRepoUrl, setGithubRepoUrl] = useState('');
  const [githubBusy, setGithubBusy] = useState(false);
  const [githubError, setGithubError] = useState(null);
  const [pdfUploading, setPdfUploading] = useState(false);
  const [pdfError, setPdfError] = useState(null);
  const [selectedTodo, setSelectedTodo] = useState(null); // { projectId, todoIndex }
  const [viewMode, setViewMode] = useState('active'); // 'active' | 'hidden' | 'archived'
  const [boardMenuOpen, setBoardMenuOpen] = useState(false);
  const [draggingProjectId, setDraggingProjectId] = useState(null);
  const [dragOverProjectId, setDragOverProjectId] = useState(null);
  const fileInputRefs = useRef({});
  const boardMenuRef = useRef(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUserId(u ? u.uid : null);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!userId) return;
    const off = createRealtimeListener(`users/${userId}/integrations/github`, (snap) => {
      setGithubIntegration(snap.val() || null);
    });
    return () => off && off();
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    const off = createRealtimeListener(`users/${userId}/projects`, (snapshot) => {
      const data = snapshot.val();
      if (!data) {
        setProjects([]);
        return;
      }
      const parsed = Object.keys(data)
        .map((id) => {
          const p = data[id];
          return {
            id,
            name: p.name || null,
            image: p.image || null,
            industry: p.industry || '',
            bookContext: p.bookContext || '',
            archived: !!p.archived,
            archivedAt: p.archivedAt || null,
            hidden: !!p.hidden,
            hiddenAt: p.hiddenAt || null,
            aiDocs: p.aiDocs && typeof p.aiDocs === 'object' ? p.aiDocs : {},
            github: p.github || null,
            order: typeof p.order === 'number' ? p.order : null,
            createdAt: p.createdAt || 0,
            todos: Array.isArray(p.todos) ? p.todos : [],
          };
        })
        .filter((p) => p.name)
        .sort((a, b) => {
          const ao = a.order ?? a.createdAt ?? 0;
          const bo = b.order ?? b.createdAt ?? 0;
          return ao - bo;
        });
      setProjects(parsed);
    });
    return () => off && off();
  }, [userId]);

  const addTodo = async (projectId) => {
    const text = (newTodoText[projectId] || '').trim();
    const dueDate = (newTodoDate[projectId] || '').trim();
    if (!text || !dueDate || !userId) return;
    const project =
      projects.find((p) => p.id === projectId) ||
      (projectId === 'delta-ai' ? { id: 'delta-ai', name: 'Delta', todos: [], isDelta: true } : null);
    if (!project) return;
    if (projectId === 'delta-ai' && !projects.some((p) => p.id === 'delta-ai')) {
      // First write to the Delta pseudo-project — seed its name so it persists like a project.
      await update(ref(database, `users/${userId}/projects/${projectId}`), {
        name: 'Delta',
        isDeltaAI: true,
        createdAt: Date.now(),
      });
    }
    const updated = [
      ...(project.todos || []),
      {
        task: text,
        completed: false,
        subTodos: [],
        dueDate,
        createdAt: Date.now(),
      },
    ];
    await set(ref(database, `users/${userId}/projects/${projectId}/todos`), updated);
    setNewTodoText({ ...newTodoText, [projectId]: '' });
    setNewTodoDate({ ...newTodoDate, [projectId]: '' });
  };

  const setTodoDueDate = async (projectId, todoIndex, dueDate) => {
    if (!userId) return;
    const project = projects.find((p) => p.id === projectId);
    if (!project) return;
    const todos = [...(project.todos || [])];
    if (!todos[todoIndex]) return;
    todos[todoIndex] = { ...todos[todoIndex], dueDate: dueDate || null };
    await set(ref(database, `users/${userId}/projects/${projectId}/todos`), todos);
  };

  const handleTodoContextMenu = (e, projectId, todoIndex, currentDueDate) => {
    e.preventDefault();
    setTodoMenu({
      x: e.clientX,
      y: e.clientY,
      projectId,
      todoIndex,
      dueDate: currentDueDate || '',
    });
  };

  useEffect(() => {
    if (!todoMenu) return;
    const close = () => setTodoMenu(null);
    window.addEventListener('click', close);
    window.addEventListener('scroll', close, true);
    return () => {
      window.removeEventListener('click', close);
      window.removeEventListener('scroll', close, true);
    };
  }, [todoMenu]);

  const toggleTodo = async (projectId, index) => {
    if (!userId) return;
    const project = projects.find((p) => p.id === projectId);
    if (!project) return;
    const todos = [...(project.todos || [])];
    const wasDone = !!todos[index]?.completed;
    todos[index] = {
      ...todos[index],
      completed: !wasDone,
      completedAt: !wasDone ? Date.now() : null,
    };
    await set(ref(database, `users/${userId}/projects/${projectId}/todos`), todos);
  };

  const handleLogoUpload = async (projectId, file) => {
    if (!file || !userId) return;
    setUploadingLogo({ ...uploadingLogo, [projectId]: true });
    try {
      const ext = file.name.split('.').pop();
      const fileName = `logo_${Date.now()}.${ext}`;
      const imgRef = storageRef(storage, `users/${userId}/projects/${projectId}/${fileName}`);
      await uploadBytes(imgRef, file);
      const url = await getDownloadURL(imgRef);
      await set(ref(database, `users/${userId}/projects/${projectId}/image`), url);
    } catch (e) {
      console.error('Logo upload failed:', e);
      alert('Failed to upload logo.');
    } finally {
      setUploadingLogo({ ...uploadingLogo, [projectId]: false });
      if (fileInputRefs.current[projectId]) {
        fileInputRefs.current[projectId].value = '';
      }
    }
  };

  const openContextEditor = (project) => {
    setEditingContext(project.id);
    setContextDraft({
      industry: project.industry || '',
      bookContext: project.bookContext || '',
    });
  };

  const saveContext = async () => {
    if (!userId || !editingContext) return;
    await set(ref(database, `users/${userId}/projects/${editingContext}/industry`), contextDraft.industry);
    await set(ref(database, `users/${userId}/projects/${editingContext}/bookContext`), contextDraft.bookContext);
    setEditingContext(null);
  };

  const handlePdfUpload = async (file) => {
    if (!file || !userId || !editingContext) return;
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      setPdfError('Only PDF files are supported.');
      return;
    }
    setPdfError(null);
    setPdfUploading(true);
    try {
      const { text, pages } = await extractPdfText(file);
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const docId = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      const path = `users/${userId}/projects/${editingContext}/pdfs/${docId}_${safeName}`;
      const sref = storageRef(storage, path);
      await uploadBytes(sref, file, { contentType: 'application/pdf' });
      const url = await getDownloadURL(sref);
      await set(ref(database, `users/${userId}/projects/${editingContext}/aiDocs/${docId}`), {
        name: file.name,
        size: file.size,
        pages,
        storagePath: path,
        url,
        text,
        addedAt: Date.now(),
      });
    } catch (e) {
      console.error('PDF upload failed:', e);
      setPdfError(e.message || 'Upload failed.');
    } finally {
      setPdfUploading(false);
    }
  };

  const removePdfDoc = async (docId, doc) => {
    if (!userId || !editingContext) return;
    try {
      if (doc?.storagePath) {
        try { await deleteObject(storageRef(storage, doc.storagePath)); } catch {}
      }
      await remove(ref(database, `users/${userId}/projects/${editingContext}/aiDocs/${docId}`));
    } catch (e) {
      console.error('Remove PDF failed:', e);
    }
  };

  const startGithubOAuth = async () => {
    setGithubError(null);
    try {
      const r = await fetch('/api/github/client-id');
      const data = await r.json();
      if (!data.clientId) {
        setGithubError('GitHub OAuth not configured. Set GITHUB_CLIENT_ID in proxy server env.');
        return;
      }
      const state = Math.random().toString(36).slice(2);
      sessionStorage.setItem('gh_oauth_state', state);
      sessionStorage.setItem('gh_oauth_project_id', editingContext || '');
      const redirect = `${window.location.origin}/oauth/github/callback`;
      const url = `https://github.com/login/oauth/authorize?client_id=${encodeURIComponent(data.clientId)}&scope=${encodeURIComponent('repo read:user')}&redirect_uri=${encodeURIComponent(redirect)}&state=${state}`;
      window.location.href = url;
    } catch (e) {
      setGithubError('Proxy server unreachable. Run `npm run proxy`.');
    }
  };

  const disconnectGithub = async () => {
    if (!userId) return;
    await remove(ref(database, `users/${userId}/integrations/github`));
    setGithubRepos([]);
  };

  const loadGithubRepos = async () => {
    if (!githubIntegration?.accessToken) return;
    setGithubBusy(true);
    setGithubError(null);
    try {
      const list = await listUserRepos(githubIntegration.accessToken);
      setGithubRepos(list);
    } catch (e) {
      setGithubError(e.message);
    } finally {
      setGithubBusy(false);
    }
  };

  const connectRepoToProject = async (urlOrFullName) => {
    if (!userId || !editingContext || !githubIntegration?.accessToken) return;
    const parsed = parseRepoUrl(urlOrFullName);
    if (!parsed) {
      setGithubError('Could not parse repo. Use owner/repo or full GitHub URL.');
      return;
    }
    setGithubBusy(true);
    setGithubError(null);
    try {
      const snapshot = await fetchRepoSnapshot({ ...parsed, token: githubIntegration.accessToken });
      await set(ref(database, `users/${userId}/projects/${editingContext}/github`), snapshot);
      setGithubRepoUrl('');
    } catch (e) {
      setGithubError(e.message);
    } finally {
      setGithubBusy(false);
    }
  };

  const refreshRepoSnapshot = async () => {
    const proj = projects.find((p) => p.id === editingContext);
    if (!proj?.github?.fullName) return;
    await connectRepoToProject(proj.github.fullName);
  };

  const disconnectRepoFromProject = async () => {
    if (!userId || !editingContext) return;
    await remove(ref(database, `users/${userId}/projects/${editingContext}/github`));
  };

  const archiveProjectFromModal = async () => {
    if (!userId || !editingContext) return;
    try {
      await set(ref(database, `users/${userId}/projects/${editingContext}/archived`), true);
      await set(ref(database, `users/${userId}/projects/${editingContext}/archivedAt`), Date.now());
      setEditingContext(null);
    } catch (e) {
      console.error('Failed to archive project:', e);
      alert('Failed to archive project.');
    }
  };

  const unarchiveProject = async (projectId) => {
    if (!userId) return;
    try {
      await set(ref(database, `users/${userId}/projects/${projectId}/archived`), false);
      await set(ref(database, `users/${userId}/projects/${projectId}/archivedAt`), null);
    } catch (e) {
      console.error('Failed to restore project:', e);
      alert('Failed to restore project.');
    }
  };

  const hideProjectFromModal = async () => {
    if (!userId || !editingContext) return;
    try {
      await set(ref(database, `users/${userId}/projects/${editingContext}/hidden`), true);
      await set(ref(database, `users/${userId}/projects/${editingContext}/hiddenAt`), Date.now());
      setEditingContext(null);
    } catch (e) {
      console.error('Failed to hide project:', e);
      alert('Failed to hide project.');
    }
  };

  const unhideProject = async (projectId) => {
    if (!userId) return;
    try {
      await set(ref(database, `users/${userId}/projects/${projectId}/hidden`), false);
      await set(ref(database, `users/${userId}/projects/${projectId}/hiddenAt`), null);
    } catch (e) {
      console.error('Failed to unhide project:', e);
      alert('Failed to unhide project.');
    }
  };

  const isVisible = (p) => {
    if (viewMode === 'hidden') return !!p.hidden && !p.archived;
    if (viewMode === 'archived') return !!p.archived;
    return !p.hidden && !p.archived;
  };

  const DELTA_ID = 'delta-ai';
  const deltaInProjects = projects.some((p) => p.id === DELTA_ID);
  const deltaColumn = deltaInProjects
    ? null
    : {
        id: DELTA_ID,
        name: 'Delta',
        image: null,
        industry: '',
        bookContext: '',
        archived: false,
        archivedAt: null,
        hidden: false,
        hiddenAt: null,
        order: -Infinity,
        createdAt: 0,
        todos: [],
        aiDocs: {},
        github: null,
        isDelta: true,
      };
  const displayProjects = (() => {
    if (viewMode !== 'active') return projects;
    if (!deltaColumn) {
      const rest = projects.filter((p) => p.id !== DELTA_ID);
      const pinned = projects.find((p) => p.id === DELTA_ID);
      return pinned ? [{ ...pinned, isDelta: true }, ...rest] : projects;
    }
    return [deltaColumn, ...projects];
  })();

  useEffect(() => {
    if (!boardMenuOpen) return;
    const onClick = (e) => {
      if (boardMenuRef.current && !boardMenuRef.current.contains(e.target)) {
        setBoardMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [boardMenuOpen]);

  const deleteProjectFromModal = async () => {
    if (!userId || !editingContext) return;
    const project = projects.find((p) => p.id === editingContext);
    if (!project) return;
    const ok = window.confirm(
      `Delete project "${project.name}"? This removes all its tasks and cannot be undone.`
    );
    if (!ok) return;
    try {
      await remove(ref(database, `users/${userId}/projects/${editingContext}`));
      setEditingContext(null);
    } catch (e) {
      console.error('Failed to delete project:', e);
      alert('Failed to delete project.');
    }
  };

  const handleColumnDragStart = (e, projectId) => {
    setDraggingProjectId(projectId);
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      try {
        e.dataTransfer.setData('text/plain', projectId);
      } catch {}
    }
  };

  const handleColumnDragOver = (e, projectId) => {
    if (!draggingProjectId) return;
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
    if (draggingProjectId !== projectId) {
      setDragOverProjectId(projectId);
    }
  };

  const handleColumnDragLeave = (projectId) => {
    setDragOverProjectId((current) => (current === projectId ? null : current));
  };

  const handleColumnDragEnd = () => {
    setDraggingProjectId(null);
    setDragOverProjectId(null);
  };

  const handleColumnDrop = async (e, targetProjectId) => {
    e.preventDefault();
    const sourceId = draggingProjectId;
    setDraggingProjectId(null);
    setDragOverProjectId(null);
    if (!sourceId || !userId || sourceId === targetProjectId) return;

    const visible = projects.filter(isVisible);
    const fromIdx = visible.findIndex((p) => p.id === sourceId);
    const toIdx = visible.findIndex((p) => p.id === targetProjectId);
    if (fromIdx === -1 || toIdx === -1) return;

    const reordered = [...visible];
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);

    const updates = {};
    reordered.forEach((p, i) => {
      updates[`projects/${p.id}/order`] = i + 1;
    });
    try {
      await update(ref(database, `users/${userId}`), updates);
    } catch (err) {
      console.error('Failed to reorder projects:', err);
    }
  };

  const addProject = async () => {
    if (!userId) return;
    const name = window.prompt('New project name:');
    if (!name || !name.trim()) return;
    const projectsRef = ref(database, `users/${userId}/projects`);
    const newRef = push(projectsRef);
    const maxOrder = projects.reduce(
      (max, p) => Math.max(max, p.order ?? 0),
      0
    );
    await set(newRef, {
      name: name.trim(),
      goals: [],
      todos: [],
      order: maxOrder + 1,
      createdAt: Date.now(),
    });
  };

  const dayKey = (input) => {
    const d = typeof input === 'string' ? new Date(`${input}T00:00:00`) : new Date(input);
    if (isNaN(d.getTime())) return null;
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const now = new Date();
  const todayKey = dayKey(now);
  const tomorrowDate = new Date(now);
  tomorrowDate.setDate(tomorrowDate.getDate() + 1);
  const tomorrowKey = dayKey(tomorrowDate);

  const labelForGroup = (key) => {
    if (key === 'overdue') return 'Overdue';
    if (key === 'none') return 'No due date';
    if (key === todayKey) return 'Today';
    if (key === tomorrowKey) return 'Tomorrow';
    const [y, m, d] = key.split('-').map(Number);
    const dt = new Date(y, m - 1, d);
    const sameYear = dt.getFullYear() === now.getFullYear();
    return dt.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      ...(sameYear ? {} : { year: 'numeric' }),
    });
  };

  const groupPendingTodos = (todos) => {
    const groups = {};
    todos.forEach(({ todo, idx }) => {
      let key;
      if (!todo.dueDate) {
        key = 'none';
      } else {
        const dk = dayKey(todo.dueDate);
        if (!dk) {
          key = 'none';
        } else if (dk < todayKey) {
          key = 'overdue';
        } else {
          key = dk;
        }
      }
      if (!groups[key]) groups[key] = [];
      groups[key].push({ todo, idx });
    });
    const sortedKeys = Object.keys(groups).sort((a, b) => {
      if (a === b) return 0;
      if (a === 'overdue') return -1;
      if (b === 'overdue') return 1;
      if (a === 'none') return 1;
      if (b === 'none') return -1;
      return a < b ? -1 : 1;
    });
    // Within overdue, sort latest first (most recently overdue at top of the group)
    if (groups.overdue) {
      groups.overdue.sort((a, b) => (a.todo.dueDate < b.todo.dueDate ? 1 : -1));
    }
    return { groups, sortedKeys };
  };

  if (selectedTodo) {
    const project = projects.find((p) => p.id === selectedTodo.projectId);
    const todo = project?.todos?.[selectedTodo.todoIndex];
    if (project && todo) {
      return (
        <TodoDetailPage
          userId={userId}
          project={project}
          todoIndex={selectedTodo.todoIndex}
          todo={todo}
          onBack={() => setSelectedTodo(null)}
        />
      );
    }
  }

  return (
    <div className="todo-board-page">
      <div className="todo-board-header">
        <button className="board-back-btn" onClick={() => navigate('/app')}>
          <ArrowLeft size={18} />
          <span>Triangle</span>
        </button>
        <h1 className="board-title">
          {viewMode === 'archived' ? 'Archived Projects' : viewMode === 'hidden' ? 'Hidden Projects' : 'Todo Board'}
        </h1>
        <div className="board-header-actions" ref={boardMenuRef}>
          {viewMode !== 'active' && (
            <button
              className="board-toggle-btn"
              onClick={() => setViewMode('active')}
              title="Back to active"
            >
              <ArrowLeft size={14} />
              <span>Active</span>
            </button>
          )}
          <button
            className="board-menu-btn"
            onClick={() => setBoardMenuOpen((v) => !v)}
            title="Board menu"
            aria-haspopup="menu"
            aria-expanded={boardMenuOpen}
          >
            <MoreHorizontal size={18} />
          </button>
          {boardMenuOpen && (
            <div className="board-menu-dropdown" role="menu">
              <button
                className={`board-menu-item ${viewMode === 'hidden' ? 'active' : ''}`}
                onClick={() => { setViewMode(viewMode === 'hidden' ? 'active' : 'hidden'); setBoardMenuOpen(false); }}
              >
                <EyeOff size={14} />
                <span>Hidden</span>
                {projects.filter((p) => p.hidden && !p.archived).length > 0 && (
                  <span className="board-menu-count">{projects.filter((p) => p.hidden && !p.archived).length}</span>
                )}
              </button>
              <button
                className={`board-menu-item ${viewMode === 'archived' ? 'active' : ''}`}
                onClick={() => { setViewMode(viewMode === 'archived' ? 'active' : 'archived'); setBoardMenuOpen(false); }}
              >
                <Archive size={14} />
                <span>Archived</span>
                {projects.filter((p) => p.archived).length > 0 && (
                  <span className="board-menu-count">{projects.filter((p) => p.archived).length}</span>
                )}
              </button>
              <div className="board-menu-divider" />
              <button
                className="board-menu-item"
                onClick={() => { setBoardMenuOpen(false); addProject(); }}
              >
                <Plus size={14} />
                <span>Project</span>
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="todo-board-grid">
        {(() => {
          const visible = displayProjects.filter(isVisible);
          if (visible.length === 0) {
            return (
              <div className="board-empty">
                {viewMode === 'archived'
                  ? 'No archived projects.'
                  : viewMode === 'hidden'
                  ? 'No hidden projects.'
                  : 'No projects yet. Open the menu to add one.'}
              </div>
            );
          }
          return null;
        })()}

        {displayProjects.filter(isVisible).map((project) => {
          const pending = (project.todos || []).filter((t) => !t.completed);
          const done = (project.todos || []).filter((t) => t.completed);
          return (
            <div
              key={project.id}
              className={`todo-column ${project.archived ? 'archived' : ''} ${project.hidden ? 'hidden-project' : ''} ${project.isDelta ? 'delta-column' : ''} ${draggingProjectId === project.id ? 'dragging' : ''} ${dragOverProjectId === project.id ? 'drag-over' : ''}`}
              onDragOver={(e) => !project.isDelta && handleColumnDragOver(e, project.id)}
              onDragLeave={() => handleColumnDragLeave(project.id)}
              onDrop={(e) => !project.isDelta && handleColumnDrop(e, project.id)}
            >
              <div
                className="todo-column-header"
                draggable={!project.isDelta}
                onDragStart={(e) => !project.isDelta && handleColumnDragStart(e, project.id)}
                onDragEnd={handleColumnDragEnd}
                title={project.isDelta ? 'Delta — your central project' : 'Drag to reorder'}
              >
                <div
                  className="column-logo"
                  onClick={() => fileInputRefs.current[project.id]?.click()}
                  title="Upload logo"
                >
                  {project.image ? (
                    <img src={project.image} alt={project.name} />
                  ) : (
                    <div className="column-logo-placeholder">
                      <Upload size={20} />
                    </div>
                  )}
                  {uploadingLogo[project.id] && <div className="logo-loading">…</div>}
                </div>
                <input
                  ref={(el) => {
                    fileInputRefs.current[project.id] = el;
                  }}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={(e) => handleLogoUpload(project.id, e.target.files?.[0])}
                />
                <div className="column-title">{project.name}</div>
                {project.archived ? (
                  <button
                    className="column-settings-btn"
                    onClick={() => unarchiveProject(project.id)}
                    title="Restore project"
                  >
                    <ArchiveRestore size={14} />
                  </button>
                ) : project.hidden ? (
                  <button
                    className="column-settings-btn"
                    onClick={() => unhideProject(project.id)}
                    title="Unhide project"
                  >
                    <Eye size={14} />
                  </button>
                ) : (
                  <button
                    className="column-settings-btn"
                    onClick={() => openContextEditor(project)}
                    title="Edit industry / book context"
                  >
                    <SettingsIcon size={14} />
                  </button>
                )}
              </div>

              <div className="todo-column-body">
                {pending.length === 0 && done.length === 0 && (
                  <div className="todo-empty">No tasks yet.</div>
                )}
                {(() => {
                  const pendingWithIdx = pending.map((todo) => ({
                    todo,
                    idx: (project.todos || []).indexOf(todo),
                  }));
                  const { groups, sortedKeys } = groupPendingTodos(pendingWithIdx);
                  return sortedKeys.map((key) => (
                    <div key={key} className="todo-date-group">
                      <div className={`todo-date-header ${key === 'overdue' ? 'overdue' : ''} ${key === 'none' ? 'no-date' : ''}`}>
                        {labelForGroup(key)}
                        <span className="todo-date-count">{groups[key].length}</span>
                      </div>
                      {groups[key].map(({ todo, idx }) => (
                        <div
                          key={idx}
                          className="todo-item"
                          onContextMenu={(e) =>
                            handleTodoContextMenu(e, project.id, idx, todo.dueDate)
                          }
                        >
                          <button
                            className="todo-check"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleTodo(project.id, idx);
                            }}
                            aria-label="Complete"
                          />
                          <button
                            className="todo-task-btn"
                            onClick={() =>
                              setSelectedTodo({ projectId: project.id, todoIndex: idx })
                            }
                          >
                            {todo.task}
                            {Array.isArray(todo.subTodos) && todo.subTodos.length > 0 && (
                              <span className="todo-sub-count">
                                {todo.subTodos.filter((s) => s.completed).length}/
                                {todo.subTodos.length}
                              </span>
                            )}
                          </button>
                        </div>
                      ))}
                    </div>
                  ));
                })()}

                {done.length > 0 && (
                  <div className="todo-done-section">
                    <div className="todo-done-label">Done</div>
                    {done.map((todo) => {
                      const idx = (project.todos || []).indexOf(todo);
                      return (
                        <div
                          key={idx}
                          className="todo-item todo-item-done"
                          onContextMenu={(e) =>
                            handleTodoContextMenu(e, project.id, idx, todo.dueDate)
                          }
                        >
                          <button
                            className="todo-check todo-check-done"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleTodo(project.id, idx);
                            }}
                            aria-label="Uncomplete"
                          >
                            <Check size={12} />
                          </button>
                          <button
                            className="todo-task-btn todo-task-done"
                            onClick={() => setSelectedTodo({ projectId: project.id, todoIndex: idx })}
                          >
                            {todo.task}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {!project.archived && (
                <div className="todo-column-footer">
                  <input
                    className="todo-add-input"
                    type="text"
                    placeholder="Add a task…"
                    value={newTodoText[project.id] || ''}
                    onChange={(e) =>
                      setNewTodoText({ ...newTodoText, [project.id]: e.target.value })
                    }
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') addTodo(project.id);
                    }}
                  />
                  <div className="todo-add-row">
                    <input
                      className="todo-add-date"
                      type="date"
                      required
                      value={newTodoDate[project.id] || ''}
                      onChange={(e) =>
                        setNewTodoDate({ ...newTodoDate, [project.id]: e.target.value })
                      }
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') addTodo(project.id);
                      }}
                      title="Due date (required)"
                    />
                    <button
                      className="todo-add-btn"
                      onClick={() => addTodo(project.id)}
                      disabled={
                        !(newTodoText[project.id] || '').trim() ||
                        !(newTodoDate[project.id] || '').trim()
                      }
                      title={
                        !(newTodoDate[project.id] || '').trim()
                          ? 'Pick a due date to add'
                          : 'Add task'
                      }
                    >
                      <Plus size={14} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {todoMenu && (
        <div
          className="todo-context-menu"
          style={{ top: todoMenu.y, left: todoMenu.x }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="todo-context-label">Due date</div>
          <input
            type="date"
            autoFocus
            value={todoMenu.dueDate}
            onChange={async (e) => {
              const v = e.target.value;
              setTodoMenu({ ...todoMenu, dueDate: v });
              await setTodoDueDate(todoMenu.projectId, todoMenu.todoIndex, v);
              setTodoMenu(null);
            }}
          />
          {todoMenu.dueDate && (
            <button
              className="todo-context-clear"
              onClick={async () => {
                await setTodoDueDate(todoMenu.projectId, todoMenu.todoIndex, '');
                setTodoMenu(null);
              }}
            >
              Clear date
            </button>
          )}
        </div>
      )}

      {editingContext && (
        <div className="context-modal-backdrop" onClick={() => setEditingContext(null)}>
          <div className="context-modal" onClick={(e) => e.stopPropagation()}>
            <h3>AI Context</h3>
            <label>Industry</label>
            <input
              type="text"
              value={contextDraft.industry}
              onChange={(e) => setContextDraft({ ...contextDraft, industry: e.target.value })}
              placeholder="e.g. SaaS, B2B fintech, hardware…"
            />
            <label>Book context</label>
            <textarea
              value={contextDraft.bookContext}
              onChange={(e) => setContextDraft({ ...contextDraft, bookContext: e.target.value })}
              placeholder="e.g. The Lean Startup, Zero to One, Hooked…"
              rows={4}
            />

            {(() => {
              const project =
                projects.find((p) => p.id === editingContext) ||
                displayProjects.find((p) => p.id === editingContext);
              const docs = project?.aiDocs ? Object.entries(project.aiDocs) : [];
              const repo = project?.github;
              return (
                <>
                  <div className="ai-context-section">
                    <div className="ai-context-section-header">
                      <FileText size={14} />
                      <span>Reference PDFs</span>
                    </div>
                    <div className="ai-context-pdf-list">
                      {docs.length === 0 && (
                        <div className="ai-context-empty">No PDFs uploaded yet.</div>
                      )}
                      {docs.map(([id, doc]) => (
                        <div key={id} className="ai-context-pdf-item">
                          <FileText size={12} />
                          <a href={doc.url} target="_blank" rel="noreferrer" className="ai-context-pdf-name">{doc.name}</a>
                          <span className="ai-context-pdf-meta">{doc.pages} pages</span>
                          <button
                            className="ai-context-pdf-remove"
                            onClick={() => removePdfDoc(id, doc)}
                            title="Remove"
                          >
                            <XIcon size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                    <label className="ai-context-upload-btn">
                      <Upload size={14} />
                      <span>{pdfUploading ? 'Uploading…' : 'Upload PDF'}</span>
                      <input
                        type="file"
                        accept="application/pdf,.pdf"
                        style={{ display: 'none' }}
                        disabled={pdfUploading}
                        onChange={async (e) => {
                          const f = e.target.files?.[0];
                          if (f) await handlePdfUpload(f);
                          e.target.value = '';
                        }}
                      />
                    </label>
                    {pdfError && <div className="ai-context-error">{pdfError}</div>}
                  </div>

                  <div className="ai-context-section">
                    <div className="ai-context-section-header">
                      <Github size={14} />
                      <span>GitHub codebase</span>
                    </div>
                    {!githubIntegration ? (
                      <button className="ai-context-github-connect" onClick={startGithubOAuth}>
                        <Github size={14} />
                        <span>Connect GitHub</span>
                      </button>
                    ) : repo ? (
                      <div className="ai-context-repo">
                        <div className="ai-context-repo-row">
                          <LinkIcon size={12} />
                          <a href={`https://github.com/${repo.fullName}`} target="_blank" rel="noreferrer" className="ai-context-repo-name">{repo.fullName}</a>
                          <span className="ai-context-repo-meta">{repo.language || ''}</span>
                        </div>
                        <div className="ai-context-repo-meta-row">
                          {repo.files?.length || 0} files snapshotted · {new Date(repo.fetchedAt).toLocaleString()}
                        </div>
                        <div className="ai-context-repo-actions">
                          <button onClick={refreshRepoSnapshot} disabled={githubBusy}>
                            <RefreshCw size={12} className={githubBusy ? 'spin' : ''} />
                            <span>Refresh</span>
                          </button>
                          <button onClick={disconnectRepoFromProject}>
                            <XIcon size={12} />
                            <span>Disconnect repo</span>
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="ai-context-repo-input">
                          <input
                            type="text"
                            placeholder="owner/repo or https://github.com/owner/repo"
                            value={githubRepoUrl}
                            onChange={(e) => setGithubRepoUrl(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter' && githubRepoUrl.trim()) connectRepoToProject(githubRepoUrl.trim()); }}
                          />
                          <button
                            onClick={() => githubRepoUrl.trim() && connectRepoToProject(githubRepoUrl.trim())}
                            disabled={!githubRepoUrl.trim() || githubBusy}
                          >
                            {githubBusy ? 'Fetching…' : 'Connect repo'}
                          </button>
                        </div>
                        <div className="ai-context-repo-browse">
                          <button onClick={loadGithubRepos} disabled={githubBusy}>
                            {githubBusy ? 'Loading…' : githubRepos.length ? 'Reload repo list' : 'Browse my repos'}
                          </button>
                          <button className="link" onClick={disconnectGithub}>Disconnect GitHub</button>
                        </div>
                        {githubRepos.length > 0 && (
                          <div className="ai-context-repo-list">
                            {githubRepos.slice(0, 30).map((r) => (
                              <button
                                key={r.fullName}
                                className="ai-context-repo-list-item"
                                onClick={() => connectRepoToProject(r.fullName)}
                                disabled={githubBusy}
                                title={r.description || ''}
                              >
                                <span className="ai-context-repo-list-name">{r.fullName}</span>
                                {r.private && <span className="ai-context-repo-badge">private</span>}
                              </button>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                    {githubError && <div className="ai-context-error">{githubError}</div>}
                  </div>
                </>
              );
            })()}

            <div className="context-modal-actions">
              <div className="context-modal-actions-left">
                {editingContext !== 'delta-ai' && (
                  <>
                    <button onClick={hideProjectFromModal}>
                      <EyeOff size={14} />
                      <span>Hide</span>
                    </button>
                    <button onClick={archiveProjectFromModal}>
                      <Archive size={14} />
                      <span>Archive</span>
                    </button>
                    <button className="danger" onClick={deleteProjectFromModal}>
                      <Trash2 size={14} />
                      <span>Delete</span>
                    </button>
                  </>
                )}
              </div>
              <div className="context-modal-actions-right">
                <button onClick={() => setEditingContext(null)}>Cancel</button>
                <button className="primary" onClick={saveContext}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TodoBoardPage;
